/* https://benhoneywill.com/building-a-range-slider-component-in-react/ */

import React from 'react';

import KeyboardNavigationContainerDesktop from '@/core/components/dropdown/keyboard-navigation-container-desktop';
import KeyboardNavigationItemDesktop from '@/core/components/dropdown/keyboard-navigation-item-desktop';
import color from '@/core/features/styles/color';

type RangeSliderProps = {
    disableTabIndex?: boolean;
    keyboardNavigationContainerId: string;
    max: number;
    min: number;
    onChange: (values: { max: number; min: number }) => void;
    onKeyboardActive?: () => void;
    step?: number;
    value: { max: number; min: number };
};

/** In short: we have two input controls that determine the min and max values.
 * These two inputs are made invisible and replaced by the controlWrapper
 * since we only need them to determine the two min & max values.
 * We then take the  min & max values of the inputs to set
 * the position of the nodes in css of the controlWrapper which replaces the two inputs.  */
export default function RangeSliderDesktop({
    disableTabIndex = false,
    keyboardNavigationContainerId,
    max,
    min,
    onChange,
    onKeyboardActive,
    step = 1,
    value,
}: RangeSliderProps) {
    const railRef = React.useRef<HTMLDivElement | null>(null);
    const rangeMiddle = max - min;
    const [minValue, setMinValue] = React.useState(value ? value.min : min);
    const [maxValue, setMaxValue] = React.useState(value ? value.max : max);

    const [isMinControlHighlighted, setIsMinControlHighlighted] = React.useState(false);
    const [isMaxControlHighlighted, setIsMaxControlHighlighted] = React.useState(false);

    const minControlNodePosition = ((minValue - min) / rangeMiddle) * 100;
    const maxControlNodePosition = ((maxValue - min) / rangeMiddle) * 100;

    React.useEffect(() => {
        if (value) {
            setMinValue(value.min);
            setMaxValue(value.max);
        }
    }, [value]);

    const handleMinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        const newMinVal = Math.min(parseInt(event.target.value), maxValue - step);
        if (!value) {
            setMinValue(newMinVal);
        }
        onChange({ max: maxValue, min: newMinVal });
    };

    const minChangeByKeyboard = (amount: number) => {
        const newMinVal = Math.max(min, Math.min(minValue + amount, maxValue - step));
        if (!value) {
            setMinValue(newMinVal);
        }
        onChange({ max: maxValue, min: newMinVal });
    };

    const handleMaxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        const newMaxVal = Math.max(parseInt(event.target.value), minValue + step);
        if (!value) {
            setMaxValue(newMaxVal);
        }
        onChange({ max: newMaxVal, min: minValue });
    };

    const maxChangeByKeyboard = (amount: number) => {
        const newMaxVal = Math.min(max, Math.max(maxValue + amount, minValue + step));
        if (!value) {
            setMaxValue(newMaxVal);
        }
        onChange({ max: newMaxVal, min: minValue });
    };

    return (
        <div className={'rangeSlider flex relative items-center'}>
            <div className={'inputWrapper absolute'}>
                <input
                    className={'input absolute w-full h-full p-0 pointer-events-none opacity-0'}
                    max={max}
                    min={min}
                    onChange={handleMinChange}
                    step={step}
                    tabIndex={disableTabIndex ? -1 : undefined}
                    type={'range'}
                    value={minValue}
                />
                <input
                    className={'input absolute w-full h-full p-0 pointer-events-none opacity-0'}
                    max={max}
                    min={min}
                    onChange={handleMaxChange}
                    step={step}
                    tabIndex={disableTabIndex ? -1 : undefined}
                    type={'range'}
                    value={maxValue}
                />
            </div>
            <div className={'controlWrapper absolute w-full'}>
                <KeyboardNavigationContainerDesktop
                    activeOnFocus={true}
                    containerId={`${keyboardNavigationContainerId}-min`}
                    hasCustomEvents={true}
                    onBlur={() => {
                        setIsMinControlHighlighted(false);
                    }}
                    onFocus={() => {
                        onKeyboardActive?.();
                        setIsMinControlHighlighted(true);
                    }}
                >
                    <div
                        className={'control rounded-[50%] absolute bg-white z-2'}
                        style={{
                            border: isMinControlHighlighted ? '1px solid #0556C1' : undefined,
                            left: `${minControlNodePosition}%`,
                        }}
                    >
                        <KeyboardNavigationItemDesktop
                            customEventKey={'ArrowLeft'}
                            name={'decrease'}
                            onSelect={() => {
                                minChangeByKeyboard(-step);
                            }}
                        />
                        <KeyboardNavigationItemDesktop
                            customEventKey={'ArrowRight'}
                            name={'increase'}
                            onSelect={() => {
                                minChangeByKeyboard(step);
                            }}
                        />
                    </div>
                </KeyboardNavigationContainerDesktop>

                <div
                    className={'rail absolute w-full bg-dusty-gray'}
                    ref={railRef}
                >
                    <div
                        className={'absolute h-full bg-endeavour'}
                        style={{
                            left: `${minControlNodePosition}%`,
                            right: `100 - ${maxControlNodePosition}%`,
                            width: `${maxControlNodePosition - minControlNodePosition}%`,
                        }}
                    />
                </div>
                <KeyboardNavigationContainerDesktop
                    activeOnFocus={true}
                    containerId={`${keyboardNavigationContainerId}-max`}
                    hasCustomEvents={true}
                    onBlur={() => {
                        setIsMaxControlHighlighted(false);
                    }}
                    onFocus={() => {
                        onKeyboardActive?.();
                        setIsMaxControlHighlighted(true);
                    }}
                >
                    <div
                        className={'control rounded-[50%] absolute bg-white z-2'}
                        style={{
                            border: isMaxControlHighlighted ? '1px solid #0556C1' : undefined,
                            left: `${maxControlNodePosition}%`,
                        }}
                    >
                        <KeyboardNavigationItemDesktop
                            customEventKey={'ArrowLeft'}
                            name={'decrease'}
                            onSelect={() => {
                                maxChangeByKeyboard(-step);
                            }}
                        />
                        <KeyboardNavigationItemDesktop
                            customEventKey={'ArrowRight'}
                            name={'increase'}
                            onSelect={() => {
                                maxChangeByKeyboard(step);
                            }}
                        />
                    </div>
                </KeyboardNavigationContainerDesktop>
            </div>
            <style jsx>{`
                .rangeSlider {
                    --control-node-size: 27px;
                    margin: calc(var(--control-node-size) / 2);
                }
                .inputWrapper {
                    width: calc(100% + var(--control-node-size));
                    margin: 0 calc(var(--control-node-size) / -2);
                    height: var(--control-node-size);
                }
                .controlWrapper {
                    height: var(--control-node-size);
                }
                .rail {
                    top: 50%;
                    transform: translateY(-50%);
                    height: 2px;
                    border-radius: 3px;
                }
                .rail::after {
                    content: '';
                    position: absolute;
                    top: -10px;
                    bottom: -10px;
                    left: -10px;
                    right: -10px;
                }
                .control {
                    width: var(--control-node-size);
                    height: var(--control-node-size);
                    top: 50%;
                    margin-left: calc(var(--control-node-size) / -2);
                    transform: translate3d(0, -50%, 0);
                    box-shadow: 0px 1px 2px 1px ${color('black-02')};
                }
                .input {
                    appearance: none;
                    z-index: 3;
                }
                .input::-ms-track {
                    appearance: none;
                    background: transparent;
                    border: transparent;
                }
                .input::-moz-range-track {
                    appearance: none;
                    background: transparent;
                    border: transparent;
                }
                .input:focus::-webkit-slider-runnable-track {
                    appearance: none;
                    background: transparent;
                    border: transparent;
                }
                .input::-ms-thumb {
                    appearance: none;
                    pointer-events: all;
                    width: var(--control-node-size);
                    height: var(--control-node-size);
                    border-radius: unset;
                    border: 0 none;
                    cursor: grab;
                }
                .input::-ms-thumb:active {
                    cursor: grabbing;
                }
                .input::-moz-range-thumb {
                    appearance: none;
                    pointer-events: all;
                    width: var(--control-node-size);
                    height: var(--control-node-size);
                    border-radius: unset;
                    border: 0 none;
                    cursor: grab;
                }
                .input::-moz-range-thumb:active {
                    cursor: grabbing;
                }
                .input::-webkit-slider-thumb {
                    appearance: none;
                    pointer-events: all;
                    width: var(--control-node-size);
                    height: var(--control-node-size);
                    border-radius: unset;
                    border: 0 none;
                    cursor: grab;
                }
                .input::-webkit-slider-thumb:active {
                    cursor: grabbing;
                }
            `}</style>
        </div>
    );
}
